'use client';

import { useEffect } from 'react';

import { enforceOneDayTtlWhenValueChanges } from '@/utils/metaAttributionCookies';

type UseMetaAttributionCookiesOptions = {
  oneDaySeconds?: number; // default 86400
  markerTtlSeconds?: number; // default 7 days
  alsoFixFbp?: boolean; // default true
  secure?: boolean; // default false (set true if your site is always https)
  sameSite?: 'Lax' | 'Strict' | 'None'; // default 'Lax'
  debug?: boolean; // default false
};

export function useMetaAttributionCookies(opts: UseMetaAttributionCookiesOptions = {}) {
  useEffect(() => {
    const {
      oneDaySeconds,
      markerTtlSeconds,
      alsoFixFbp = true,
      secure = false,
      sameSite = 'Lax',
      debug = false,
    } = opts;

    // Fix _fbc
    const fbc = enforceOneDayTtlWhenValueChanges({
      targetName: '_fbc',
      markerValueName: '_fbc_fixed_value',
      markerAtName: '_fbc_fixed_at',
      oneDaySeconds,
      markerTtlSeconds,
      sameSite,
      secure,
    });

    // Optionally fix _fbp too (Meta browser id cookie)
    const fbp = alsoFixFbp
      ? enforceOneDayTtlWhenValueChanges({
          targetName: '_fbp',
          markerValueName: '_fbp_fixed_value',
          markerAtName: '_fbp_fixed_at',
          oneDaySeconds,
          markerTtlSeconds,
          sameSite,
          secure,
        })
      : { changed: false as const };

    if (debug && (fbc.changed || fbp.changed)) {
      // keep this lightweight; swap to your logger if you want
      // eslint-disable-next-line no-console
      console.log('[meta-cookies] enforced 1-day TTL', {
        fbcChanged: fbc.changed,
        fbpChanged: fbp.changed,
      });
    }
  }, [opts]);
}
